/**
 * HARPIA Performance — global theme behaviour.
 * Small, dependency-free enhancements shared across every page.
 */

/**
 * Shared scroll-lock counter. Several independent overlays (mobile menu,
 * filter drawer, cart drawer, product zoom) each want to lock body scroll
 * while open. A plain attribute toggle breaks if two ever overlap — the
 * one that closes first would unlock scrolling while the other is still
 * open. Defined the same guarded way in every file that needs it, so
 * whichever loads first creates it and the rest just reuse it.
 */
window.HarpiaScrollLock = window.HarpiaScrollLock || (function () {
  var locks = new Set();
  return {
    lock: function (id) {
      locks.add(id);
      document.documentElement.setAttribute('data-scroll-lock', '');
    },
    unlock: function (id) {
      locks.delete(id);
      if (locks.size === 0) document.documentElement.removeAttribute('data-scroll-lock');
    }
  };
})();

(function () {
  /**
   * Only one `<details>` popover (search, vehicle finder, mobile nav, filter
   * drawers) should be open at a time, and clicking outside or pressing
   * Escape should close whichever is open.
   */
  /**
   * Popovers (vehicle/search triggers in the header, nav dropdowns): a
   * <details data-popover> whose panel is one of
   * .site-header__popover-panel / .site-header__submenu. CSS alone
   * (@starting-style + transition-behavior: allow-discrete, see
   * header.liquid) only animates open/close in browsers new enough to
   * support it (Chrome/Edge 117+, Safari 17.4+, Firefox 129+) — anywhere
   * else the panel still snaps instantly. This drives the exact same
   * fade/rise with the Web Animations API instead, so it's consistent
   * everywhere, and takes over *closing* specifically (intercepting the
   * summary click, click-outside and Escape paths) since native
   * <details> offers no hook to animate out before content disappears.
   */
  function initPopovers() {
    var popovers = document.querySelectorAll('[data-popover]');
    if (!popovers.length) return;

    var OPEN_KEYFRAMES = [
      { opacity: 0, transform: 'translateY(-10px) scale(0.97)' },
      { opacity: 1, transform: 'translateY(0) scale(1)' },
    ];
    var ANIMATABLE = typeof Element !== 'undefined' && !!Element.prototype.animate;

    function panelFor(popover) {
      return popover.querySelector('.site-header__popover-panel, .site-header__submenu');
    }

    function closeNow(popover) {
      popover.open = false;
    }

    function closeAnimated(popover) {
      if (!popover.open) return;
      var panel = panelFor(popover);
      if (!panel || !ANIMATABLE) {
        closeNow(popover);
        return;
      }
      var animation = panel.animate(OPEN_KEYFRAMES, { duration: 150, easing: 'ease-in', direction: 'reverse' });
      animation.onfinish = function () {
        closeNow(popover);
      };
    }

    document.addEventListener('click', function (event) {
      popovers.forEach(function (popover) {
        if (popover.open && !popover.contains(event.target)) {
          closeAnimated(popover);
        }
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      popovers.forEach(function (popover) {
        if (popover.open) {
          closeAnimated(popover);
          var summary = popover.querySelector('summary');
          if (summary) summary.focus();
        }
      });
    });

    popovers.forEach(function (popover) {
      var summary = popover.querySelector('summary');
      if (summary) {
        summary.addEventListener('click', function (event) {
          if (popover.open) {
            event.preventDefault();
            closeAnimated(popover);
          }
          // Opening: let the browser toggle `open` natively — the
          // 'toggle' listener below plays the open animation.
        });
      }

      popover.addEventListener('toggle', function () {
        if (!popover.open) return;
        popovers.forEach(function (other) {
          if (other !== popover) closeNow(other);
        });
        var panel = panelFor(popover);
        if (panel && ANIMATABLE) {
          panel.animate(OPEN_KEYFRAMES, { duration: 200, easing: 'ease-out' });
        }
      });
    });
  }

  /**
   * Quantity steppers: <div data-quantity-stepper> wrapping a number
   * input plus [data-quantity-decrease] / [data-quantity-increase] buttons.
   */
  function initQuantitySteppers() {
    document.querySelectorAll('[data-quantity-stepper]').forEach(function (stepper) {
      var input = stepper.querySelector('input[type="number"]');
      var decrease = stepper.querySelector('[data-quantity-decrease]');
      var increase = stepper.querySelector('[data-quantity-increase]');
      if (!input) return;

      function setValue(next) {
        var min = parseInt(input.min, 10) || 1;
        input.value = Math.max(min, next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (decrease) {
        decrease.addEventListener('click', function () {
          setValue((parseInt(input.value, 10) || 1) - 1);
        });
      }

      if (increase) {
        increase.addEventListener('click', function () {
          setValue((parseInt(input.value, 10) || 1) + 1);
        });
      }
    });
  }

  /**
   * Collection filter drawer on mobile: [data-filter-toggle] shows/hides
   * [data-filter-panel] via a `is-open` class rather than relying on a
   * <details> element, so the same panel can be styled differently on
   * desktop (always visible) vs. mobile (slide-in).
   */
  function initFilterDrawer() {
    var toggle = document.querySelector('[data-filter-toggle]');
    var panel = document.querySelector('[data-filter-panel]');
    var backdrop = document.querySelector('[data-filter-backdrop]');
    if (!toggle || !panel) return;

    function close() {
      panel.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      window.HarpiaScrollLock.unlock('filter-drawer');
    }

    function open() {
      panel.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      window.HarpiaScrollLock.lock('filter-drawer');
    }

    toggle.addEventListener('click', function () {
      panel.classList.contains('is-open') ? close() : open();
    });

    if (backdrop) backdrop.addEventListener('click', close);

    panel.querySelectorAll('[data-filter-close]').forEach(function (button) {
      button.addEventListener('click', close);
    });
  }

  /**
   * Off-canvas mobile navigation. [data-mobile-nav-toggle] opens
   * [data-mobile-nav]; the backdrop and any [data-mobile-nav-close] element
   * inside it close it again.
   */
  function initMobileNav() {
    var toggle = document.querySelector('[data-mobile-nav-toggle]');
    var nav = document.querySelector('[data-mobile-nav]');
    if (!toggle || !nav) return;

    function close() {
      nav.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      window.HarpiaScrollLock.unlock('mobile-nav');
    }

    function open() {
      nav.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      window.HarpiaScrollLock.lock('mobile-nav');
    }

    toggle.addEventListener('click', function () {
      nav.classList.contains('is-open') ? close() : open();
    });

    nav.querySelectorAll('[data-mobile-nav-close]').forEach(function (element) {
      element.addEventListener('click', close);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && nav.classList.contains('is-open')) close();
    });
  }

  /**
   * Scroll-reveal: fades/slides elements marked [data-reveal] into place
   * as they enter the viewport. The `.reveal` class (which does the actual
   * hiding) is only added here, right before observing — so if JS never
   * runs, or IntersectionObserver isn't supported, content simply renders
   * normally instead of getting stuck invisible.
   */
  function initScrollReveal() {
    var targets = document.querySelectorAll('[data-reveal]');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      // Positive bottom margin: reveal content shortly *before* it
      // scrolls into view (not after it's already well inside), so it's
      // done fading in by the time it's actually seen. The previous
      // negative margin required scrolling well past an element before
      // revealing it, which on a short mobile viewport could make a grid
      // look like it ended — the next row was still sitting at opacity:0
      // just below the fold.
      { rootMargin: '0px 0px 200px 0px', threshold: 0.01 }
    );

    targets.forEach(function (el) {
      el.classList.add('reveal');
      observer.observe(el);
    });

    // IntersectionObserver's first callback can land a frame or more after
    // observe() runs. For content already in the viewport at load (hero,
    // category tiles, the first row of a grid), that gap shows up as a
    // flash of invisible content — or worse, stays stuck if anything else
    // delays further script execution. Resolve those immediately instead
    // of waiting on the observer at all.
    window.requestAnimationFrame(function () {
      targets.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          el.classList.add('is-visible');
        }
      });
    });
  }

  /**
   * Smoothly animates <details data-accordion> open/close instead of the
   * browser's instant default. Keeps real <details>/<summary> semantics
   * (keyboard, screen readers) — only the height transition is custom,
   * using the Web Animations API since `height: auto` can't be animated
   * directly with CSS transitions.
   */
  function initAnimatedAccordions() {
    document.querySelectorAll('[data-accordion]').forEach(function (details) {
      var summary = details.querySelector('summary');
      var content = details.querySelector('[data-accordion-content]');
      if (!summary || !content || !details.animate) return;

      var animation = null;
      var isClosing = false;
      var isExpanding = false;

      function onFinish(open) {
        details.open = open;
        animation = null;
        isClosing = false;
        isExpanding = false;
        details.style.height = '';
        details.style.overflow = '';
      }

      function shrink() {
        isClosing = true;
        var startHeight = details.offsetHeight + 'px';
        var endHeight = summary.offsetHeight + 'px';

        if (animation) animation.cancel();

        animation = details.animate({ height: [startHeight, endHeight] }, { duration: 250, easing: 'ease-out' });
        animation.onfinish = function () { onFinish(false); };
        animation.oncancel = function () { isClosing = false; };
      }

      function expand() {
        isExpanding = true;
        var startHeight = details.offsetHeight + 'px';
        var endHeight = summary.offsetHeight + content.offsetHeight + 'px';

        if (animation) animation.cancel();

        animation = details.animate({ height: [startHeight, endHeight] }, { duration: 250, easing: 'ease-out' });
        animation.onfinish = function () { onFinish(true); };
        animation.oncancel = function () { isExpanding = false; };
      }

      function open() {
        details.style.height = details.offsetHeight + 'px';
        details.open = true;
        window.requestAnimationFrame(expand);
      }

      summary.addEventListener('click', function (event) {
        event.preventDefault();
        details.style.overflow = 'hidden';

        if (details.open && !isClosing) {
          shrink();
        } else if (!details.open || isClosing) {
          open();
        }
      });
    });
  }

  /**
   * "Confirmed to fit" popups on product cards (collection grids, homepage,
   * search, recommendations). Delegated on document so it also covers cards
   * injected later (e.g. the async product-recommendations fetch).
   */
  function initFitmentModals() {
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-fitment-trigger]');
      if (trigger) {
        var modal = document.getElementById(trigger.getAttribute('data-fitment-trigger'));
        if (modal && modal.showModal) modal.showModal();
        return;
      }

      var closeButton = event.target.closest('[data-fitment-close]');
      if (closeButton) {
        var openDialog = closeButton.closest('dialog');
        if (openDialog) openDialog.close();
        return;
      }

      // A click landing on the <dialog> element itself (not a descendant)
      // means it hit the ::backdrop — close on that, like any modal.
      if (event.target.matches('dialog[data-fitment-modal]')) {
        event.target.close();
      }
    });
  }

  /**
   * "Send an inquiry" popup for sold-out products (product.liquid and
   * product-card.liquid). Same open/close wiring as initFitmentModals,
   * plus a fetch-based submit — see inquiry-modal.liquid's header
   * comment for why not Shopify's native {% form %}.
   */
  function initInquiryModal() {
    var modals = document.querySelectorAll('[data-inquiry-modal]');
    if (!modals.length) return;

    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-inquiry-open]');
      if (trigger) {
        var modal = document.getElementById(trigger.getAttribute('data-inquiry-open'));
        if (modal && modal.showModal) modal.showModal();
        return;
      }

      var closeButton = event.target.closest('[data-inquiry-close]');
      if (closeButton) {
        var openDialog = closeButton.closest('dialog');
        if (openDialog) openDialog.close();
        return;
      }

      if (event.target.matches('dialog[data-inquiry-modal]')) {
        event.target.close();
      }
    });

    // Submits via fetch to the Cloudflare Worker (see inquiry-modal.liquid's
    // header comment for why not Shopify's native contact form) instead of
    // a real page submission — every instance's success/error state lives
    // only in its own DOM subtree, so sending one product's inquiry can
    // never make a DIFFERENT product's button look already-sent too.
    document.querySelectorAll('[data-inquiry-form]').forEach(function (form) {
      var endpoint = form.getAttribute('data-endpoint');
      var logoUrl = form.getAttribute('data-logo-url');
      var shopUrl = form.getAttribute('data-shop-url');
      var productTitle = form.getAttribute('data-product-title');
      var productUrl = form.getAttribute('data-product-url');
      var fieldsWrap = form.querySelector('[data-inquiry-fields]');
      var successBox = form.querySelector('[data-inquiry-success]');
      var errorBox = form.querySelector('[data-inquiry-error]');
      var submitButton = form.querySelector('[data-inquiry-submit]');

      form.addEventListener('submit', function (event) {
        event.preventDefault();
        errorBox.hidden = true;

        if (!form.checkValidity()) {
          form.reportValidity();
          return;
        }

        var formData = new FormData(form);
        var payload = { type: 'stock-inquiry', productTitle: productTitle, productUrl: productUrl, logoUrl: logoUrl, shopUrl: shopUrl };
        formData.forEach(function (value, key) {
          payload[key] = value;
        });

        submitButton.disabled = true;
        var sendingLabel = submitButton.getAttribute('data-label-sending');
        if (sendingLabel) submitButton.textContent = sendingLabel;

        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
          .then(function (response) {
            return response.json().then(function (data) {
              return { ok: response.ok && data.ok, data: data };
            });
          })
          .then(function (result) {
            if (!result.ok) throw new Error('request failed');
            fieldsWrap.hidden = true;
            successBox.hidden = false;
          })
          .catch(function () {
            errorBox.textContent = errorBox.getAttribute('data-fallback-text');
            errorBox.hidden = false;
            submitButton.disabled = false;
            var defaultLabel = submitButton.getAttribute('data-label-default');
            if (defaultLabel) submitButton.textContent = defaultLabel;
          });
      });
    });
  }

  /**
   * "Our Partners" logo marquee (our-partners.liquid) — driven by rAF and
   * continuous modulo math instead of a CSS @keyframes loop. Two reasons:
   * 1. A CSS animation starts counting from page load, so by the time a
   *    visitor actually scrolls to it, it's already partway through its
   *    cycle — looked like it "always starts from the middle." This
   *    starts the clock only once the strip first scrolls into view.
   * 2. `offset = (offset + speed * dt) % halfWidth` never restarts a
   *    keyframe iteration, so there's no loop-boundary moment for a
   *    browser to stutter on — some do show a brief hitch resetting a
   *    CSS `animation: ... infinite` at its iteration boundary.
   */
  function initMarquee() {
    var track = document.querySelector('[data-marquee]');
    if (!track) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!window.requestAnimationFrame) return;

    var halfWidth = track.scrollWidth / 2;
    if (!halfWidth) return;
    var pxPerSecond = halfWidth / 32; // same pace as the old 32s-per-cycle CSS animation
    var offset = 0;
    var lastTime = null;
    var paused = false;
    var started = false;

    track.addEventListener('focusin', function () { paused = true; });
    track.addEventListener('focusout', function () { paused = false; });

    function tick(timestamp) {
      if (lastTime === null) lastTime = timestamp;
      var delta = (timestamp - lastTime) / 1000;
      lastTime = timestamp;
      if (!paused) {
        offset = (offset + pxPerSecond * delta) % halfWidth;
        track.style.transform = 'translateX(-' + offset + 'px)';
      }
      window.requestAnimationFrame(tick);
    }

    function start() {
      if (started) return;
      started = true;
      window.requestAnimationFrame(tick);
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) start();
        });
      }, { threshold: 0 });
      observer.observe(track);
    } else {
      start();
    }
  }

  /**
   * Click-to-load YouTube embed (video-feature.liquid) — swaps the static
   * thumbnail for the real iframe only once clicked, so the section never
   * pulls in YouTube's player on a page view that doesn't watch it.
   */
  function initVideoFeature() {
    document.querySelectorAll('[data-video-player]').forEach(function (player) {
      var playButton = player.querySelector('[data-video-play]');
      if (!playButton) return;

      playButton.addEventListener('click', function () {
        var youtubeId = player.getAttribute('data-youtube-id');
        var iframe = document.createElement('iframe');
        iframe.src = 'https://www.youtube.com/embed/' + youtubeId + '?autoplay=1&rel=0';
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        iframe.allowFullscreen = true;
        player.innerHTML = '';
        player.appendChild(iframe);
      });
    });
  }

  /**
   * Branded image loading state (see the `img` rules in critical.css):
   * marks every <img> `.is-loaded` once it has actually loaded (stopping
   * the shimmer, letting the real photo show), or `.is-loaded.is-broken`
   * if it failed — swapping its src for a transparent pixel so the
   * browser's native broken-image icon never appears, leaving just the
   * branded placeholder filling the box. Covers images already on the
   * page at init *and* ones added later (lazy-loaded cards, the async
   * recommendations fetch, the fitment popup) via a capturing listener,
   * since `load`/`error` don't bubble.
   */
  function initImageLoadingState() {
    var TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBTAA7';

    function markLoaded(img) {
      img.classList.add('is-loaded');
    }

    function markBroken(img) {
      img.classList.add('is-loaded', 'is-broken');
      img.src = TRANSPARENT_PIXEL;
    }

    document.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) return;
      if (img.naturalWidth === 0) {
        markBroken(img);
      } else {
        markLoaded(img);
      }
    });

    document.addEventListener('load', function (event) {
      if (event.target.tagName === 'IMG') markLoaded(event.target);
    }, true);

    document.addEventListener('error', function (event) {
      if (event.target.tagName === 'IMG') markBroken(event.target);
    }, true);
  }

  function init() {
    initPopovers();
    initQuantitySteppers();
    initFilterDrawer();
    initMobileNav();
    initScrollReveal();
    initAnimatedAccordions();
    initFitmentModals();
    initInquiryModal();
    initMarquee();
    initVideoFeature();
    initImageLoadingState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
